import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle, CheckCircle2, XCircle, Clock, Gem, Info, Wallet, Copy } from 'lucide-react'
import useCryptoWithdrawStore from '../store/useCryptoWithdrawStore'
import useAppStore from '../../../store/useAppStore'
import toast from 'react-hot-toast'

/**
 * Проверка адреса по регулярке из ответа API (address_regex без делимитеров и якорей).
 * Регулярку не задали / не скомпилировалась — не блокируем отправку, проверит бэкенд.
 */
const makeAddressCheck = (net) => {
  if (!net?.address_regex) { return function () { return true } }
  try {
    const re = new RegExp('^(?:' + net.address_regex + ')$')
    return function (addr) { return re.test(addr.trim()) }
  } catch (e) {
    return function () { return true }
  }
}

/**
 * CryptoWithdraw — компонент вывода кристаллов в криптовалюте (USDT)
 * Содержит форму (валюта, сеть, адрес кошелька, кристаллы), live-расчёт суммы в USDT,
 * блок активной заявки, предупреждение о ручной выплате и историю заявок
 */
const EMPTY_LIST = []

const CryptoWithdraw = () => {
  const { t } = useTranslation()
  const { balance, loadProfile } = useAppStore()
  const { info, history, isLoading, isSubmitting, loadData, submitCryptoWithdraw, stopPolling } = useCryptoWithdrawStore()

  const [assetCode, setAssetCode] = useState('')
  const [networkCode, setNetworkCode] = useState('')
  const [address, setAddress] = useState('')
  const [crystals, setCrystals] = useState('')
  const [validationError, setValidationError] = useState(null)

  // Загрузка данных при монтировании
  useEffect(() => {
    loadData()
    return () => stopPolling()
  }, [loadData, stopPolling])

  // Справочники валют и сетей — строго из ответа, не хардкодим
  const assets = info?.assets || EMPTY_LIST
  const selectedAsset = assets.find((a) => a.asset === assetCode) || assets[0]
  const networks = selectedAsset?.networks || EMPTY_LIST
  const selectedNetwork = networks.find((n) => n.code === networkCode) || networks[0]

  // Дефолтная валюта при загрузке справочника
  useEffect(() => {
    if (assets.length > 0 && !assets.some((a) => a.asset === assetCode)) {
      setAssetCode(assets[0].asset)
    }
  }, [assets, assetCode])

  // Дефолтная сеть при смене валюты
  useEffect(() => {
    if (networks.length > 0 && !networks.some((n) => n.code === networkCode)) {
      setNetworkCode(networks[0].code)
    }
  }, [networks, networkCode])

  // Подставляем последний успешно выплаченный адрес при выборе сети;
  // если у сети его нет — очищаем поле, чтобы не остался адрес другой сети
  useEffect(() => {
    setAddress(selectedNetwork?.last_address || '')
  }, [selectedNetwork?.code, selectedNetwork?.last_address])

  // Live-расчёт суммы в USDT (в центах)
  // Формула из документации: amountMinor = Math.floor(crystals * 100 / crystals_per_unit_net)
  const amountMinor = useMemo(() => {
    const crystalsNum = parseInt(crystals, 10)
    if (!crystalsNum || !info?.crystals_per_unit_net) return 0
    return Math.floor(crystalsNum * 100 / info.crystals_per_unit_net)
  }, [crystals, info?.crystals_per_unit_net])

  // Проверка адреса по регулярке выбранной сети
  const addressCheck = useMemo(
    () => makeAddressCheck(selectedNetwork),
    [selectedNetwork]
  )

  // Валидация формы
  const validate = () => {
    const trimmedAddress = address.trim()
    if (!trimmedAddress || !addressCheck(trimmedAddress)) {
      setValidationError(t('withdraw.crypto.invalidAddress', {
        defaultValue: 'Неверный адрес кошелька для сети {{network}}',
        network: selectedNetwork?.title || selectedNetwork?.chain_title || '',
      }))
      return false
    }

    const crystalsNum = parseInt(crystals, 10)
    if (!crystalsNum || crystalsNum < (info?.min_crystals || 0)) {
      setValidationError(t('withdraw.crypto.minAmount', 'Минимальная сумма') + ': ' + (info?.min_crystals?.toLocaleString('en-US') || ''))
      return false
    }
    if (crystalsNum > (info?.max_crystals || 0)) {
      setValidationError(t('withdraw.crypto.maxAmount', 'Максимальная сумма') + ': ' + (info?.max_crystals?.toLocaleString('en-US') || ''))
      return false
    }
    if (info?.daily_max_crystals > 0 && crystalsNum > info.daily_left_crystals) {
      setValidationError(t('withdraw.crypto.errors.dailyLimit', 'Достигнут суточный лимит вывода, попробуйте позже'))
      return false
    }
    if (crystalsNum > balance) {
      setValidationError(t('withdraw.crypto.insufficientBalance', 'Недостаточно кристаллов'))
      return false
    }

    setValidationError(null)
    return true
  }

  // Обработка отправки формы
  const handleSubmit = async (e) => {
    e?.preventDefault()

    if (!validate()) return

    try {
      const result = await submitCryptoWithdraw({
        asset: selectedAsset?.asset,
        network: selectedNetwork?.code,
        address: address.trim(),
        crystals: parseInt(crystals, 10),
      })

      toast.success(t('withdraw.crypto.successWithAmount', {
        defaultValue: 'Заявка создана! Вы получите {{amount}} {{asset}}',
        amount: result.amount,
        asset: result.asset || selectedAsset?.asset || '',
      }))

      // Обновляем баланс — кристаллы списываются сразу
      await loadProfile()

      // Очищаем поле суммы
      setCrystals('')
    } catch (error) {
      // Обработка кодов ошибок из документации §5
      const errorMsg = error.message || ''
      const errorMap = {
        'disabled': t('withdraw.crypto.errors.disabled', 'Вывод в криптовалюте временно недоступен'),
        'withdraw banned': t('withdraw.crypto.errors.withdrawBanned', 'Вывод заблокирован, обратитесь в поддержку'),
        'deposit required': t('withdraw.crypto.errors.depositRequired', 'Вывод доступен после первого пополнения баланса'),
        'active request': t('withdraw.crypto.errors.activeRequest', 'Дождитесь завершения текущей заявки'),
        'invalid network': t('withdraw.crypto.errors.invalidNetwork', 'Выберите сеть из списка'),
        'invalid address': t('withdraw.crypto.errors.invalidAddress', 'Проверьте адрес кошелька для выбранной сети'),
        'error sum': t('withdraw.crypto.errors.errorSum', 'Сумма вне лимитов'),
        'daily limit': t('withdraw.crypto.errors.dailyLimit', 'Достигнут суточный лимит вывода, попробуйте позже'),
        'insufficient balance': t('withdraw.crypto.errors.insufficientBalance', 'Недостаточно кристаллов'),
        'error backend': t('withdraw.crypto.errors.errorBackend', 'Техническая ошибка, попробуйте позже'),
      }

      const matchedMsg = Object.entries(errorMap).find(([key]) => errorMsg.includes(key))
      toast.error(matchedMsg ? matchedMsg[1] : t('withdraw.crypto.errors.errorBackend', 'Техническая ошибка, попробуйте позже'))
    }
  }

  // Копирование хеша транзакции
  const handleCopyTxHash = async (txHash) => {
    try {
      await navigator.clipboard.writeText(txHash)
      toast.success(t('withdraw.crypto.txHashCopied', 'Хеш транзакции скопирован'))
    } catch (error) {
      toast.error(t('withdraw.crypto.copyError', 'Не удалось скопировать'))
    }
  }

  // Проверка блокировки формы
  const isFormDisabled = !info?.can_withdraw || info?.has_active || isSubmitting
  // Причина блокировки формы
  const isBlockedReason = !info?.can_withdraw
    ? (info?.has_deposit === false
        ? t('withdraw.crypto.blockedNoDeposit', 'Вывод доступен после первого пополнения баланса.')
        : t('withdraw.crypto.blockedCannotWithdraw', 'Вывод недоступен. Обратитесь в поддержку.'))
    : info?.has_active
      ? t('withdraw.crypto.blockedHasActive', 'У вас уже есть активная заявка. Дождитесь её завершения.')
      : null

  // Конфигурация статусов для истории
  const getStatusConfig = (status) => {
    switch (status) {
      case 'new':
        return {
          icon: Clock,
          iconClass: 'text-blue-400',
          textClass: 'text-blue-400',
          label: t('withdraw.crypto.status.new', 'В очереди'),
          containerClass: 'border-blue-500/20 bg-blue-500/5',
        }
      case 'processing':
        return {
          icon: Loader2,
          iconClass: 'text-blue-400 animate-spin',
          textClass: 'text-blue-400',
          label: t('withdraw.crypto.status.processing', 'В обработке'),
          containerClass: 'border-blue-500/20 bg-blue-500/5',
        }
      case 'success':
        return {
          icon: CheckCircle2,
          iconClass: 'text-accent-primary',
          textClass: 'text-accent-primary',
          label: t('withdraw.crypto.status.success', 'Выплачено'),
          containerClass: 'border-accent-primary/20 bg-accent-primary/5',
        }
      case 'rejected':
        return {
          icon: XCircle,
          iconClass: 'text-red-400',
          textClass: 'text-red-400',
          label: t('withdraw.crypto.status.rejected', 'Отклонена, кристаллы возвращены'),
          containerClass: 'border-red-500/20 bg-red-500/5',
        }
      default:
        return {
          icon: Clock,
          iconClass: 'text-text-secondary',
          textClass: 'text-text-secondary',
          label: status,
          containerClass: 'border-white/10 bg-surface',
        }
    }
  }

  // Активная заявка из истории (первая с не-финальным статусом)
  const activeRequest = history.find((h) => ['new', 'processing'].includes(h.status))

  if (isLoading && !info) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={32} className="text-accent-primary animate-spin" />
      </div>
    )
  }

  if (!info?.enabled || assets.length === 0) {
    return (
      <div className="text-center py-16">
        <AlertCircle size={48} className="mx-auto text-text-secondary mb-4 opacity-50" />
        <p className="text-text-secondary">{t('withdraw.crypto.disabled', 'Вывод в криптовалюте временно недоступен')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Примечание о валюте вывода */}
      <div className="flex items-center gap-2 text-sm text-text-secondary rounded-lg bg-surface border border-white border-opacity-10 p-3">
        <Info size={16} className="text-accent-primary flex-shrink-0" />
        <span>{t('withdraw.crypto.usdtNote', 'Кристаллы будут выведены в USDT')}</span>
      </div>

      {/* Блок активной заявки */}
      {activeRequest && (
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 size={20} className="text-blue-400 animate-spin" />
            <span className="font-semibold text-blue-400">
              {t('withdraw.crypto.activeRequest', 'Активная заявка')} <bdi dir="ltr">#{activeRequest.id}</bdi>
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">
              <bdi>{activeRequest.asset}</bdi> · <bdi>{activeRequest.network_title}</bdi>
            </span>
            <bdi dir="ltr" className="font-bold text-white">
              {activeRequest.amount} {activeRequest.asset}
            </bdi>
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs text-text-secondary">
            <Gem size={12} className="inline text-accent-primary" />
            <bdi dir="ltr">{activeRequest.crystals.toLocaleString('en-US')}</bdi>
          </div>
          <p className="text-xs text-text-secondary mt-2">
            {t('withdraw.crypto.activeRequestDesc', 'Заявку обрабатывает администратор. Уведомление о завершении придёт в Telegram.')}
          </p>
        </div>
      )}

      {/* Форма вывода */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Валюта (селектор только если валют несколько) */}
        {assets.length > 1 && (
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('withdraw.crypto.asset', 'Валюта')}
            </label>
            <select
              value={selectedAsset?.asset || ''}
              onChange={(e) => setAssetCode(e.target.value)}
              disabled={isFormDisabled}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg px-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            >
              {assets.map((a) => (
                <option key={a.asset} value={a.asset}>{a.title || a.asset}</option>
              ))}
            </select>
          </div>
        )}

        {/* Сеть */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {t('withdraw.crypto.network', 'Сеть')}
          </label>
          <select
            value={selectedNetwork?.code || ''}
            onChange={(e) => setNetworkCode(e.target.value)}
            disabled={isFormDisabled}
            className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg px-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
          >
            {networks.map((n) => (
              <option key={n.code} value={n.code}>
                {n.title}{n.chain_title ? ` — ${n.chain_title}` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Адрес кошелька */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {t('withdraw.crypto.address', 'Адрес кошелька')}
          </label>
          <div className="relative">
            <Wallet size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
            <input
              type="text"
              dir="ltr"
              value={address}
              onChange={(e) => setAddress(e.target.value.trim())}
              disabled={isFormDisabled}
              placeholder={selectedNetwork?.chain_title || 'TRC-20'}
              maxLength={128}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
          <p className="text-xs text-text-secondary mt-1.5">
            {t('withdraw.crypto.addressHint', 'Указывайте адрес только сети {{network}}.', { network: selectedNetwork?.title || '' })}
          </p>
        </div>

        {/* Сумма в кристаллах */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {t('withdraw.crypto.amount', 'Сумма (кристаллы)')}
          </label>
          <div className="relative">
            <Gem size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
            <input
              type="text"
              dir="ltr"
              inputMode="numeric"
              value={crystals ? Number(crystals).toLocaleString('en-US') : ''}
              onChange={(e) => setCrystals(e.target.value.replace(/\D/g, ''))}
              disabled={isFormDisabled}
              placeholder={info?.min_crystals?.toLocaleString('en-US') || '0'}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => info?.min_crystals && setCrystals(String(info.min_crystals))}
              disabled={isFormDisabled}
              className="text-text-secondary hover:text-accent-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('withdraw.crypto.min', 'Мин.')}: {info?.min_crystals?.toLocaleString('en-US') || '—'}
            </button>
            <button
              type="button"
              onClick={() => info?.max_crystals && setCrystals(String(Math.min(info.max_crystals, balance)))}
              disabled={isFormDisabled}
              className="text-text-secondary hover:text-accent-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('withdraw.crypto.max', 'Макс.')}: {info?.max_crystals?.toLocaleString('en-US') || '—'}
            </button>
          </div>
        </div>

        {/* Расчёт суммы в криптовалюте */}
        {amountMinor > 0 && (
          <div className="rounded-lg bg-accent-primary/10 border border-accent-primary/20 p-4 text-center">
            <p className="text-sm text-text-secondary mb-1">{t('withdraw.crypto.youWillReceive', 'Вы получите')}</p>
            <p className="text-3xl font-bold text-accent-primary">
              <bdi dir="ltr">{(amountMinor / 100).toFixed(2)}</bdi> <bdi className="text-xl">{selectedAsset?.title || selectedAsset?.asset}</bdi>
            </p>
          </div>
        )}

        {/* Предупреждение о ручной выплате */}
        <div className="flex items-start gap-2 text-xs text-yellow-400 rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3">
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          <span>{t('withdraw.crypto.approvalWarning', 'Проверьте адрес и сеть: перевод на ошибочный адрес невозможно вернуть.')}</span>
        </div>

        {/* Ошибка валидации */}
        {validationError && (
          <div className="flex items-center gap-2 text-sm text-red-400">
            <AlertCircle size={16} />
            <span>{validationError}</span>
          </div>
        )}

        {/* Причина блокировки формы */}
        {isBlockedReason && (
          <div className="flex items-center gap-2 text-sm text-yellow-400 rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3">
            <AlertCircle size={16} />
            <span>{isBlockedReason}</span>
          </div>
        )}

        {/* Кнопка отправки */}
        <button
          type="submit"
          disabled={isFormDisabled}
          className="w-full py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {t('withdraw.crypto.submitting', 'Создание заявки...')}
            </>
          ) : (
            t('withdraw.crypto.submit', 'Вывести')
          )}
        </button>
      </form>

      {/* История заявок */}
      {history.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">
            {t('withdraw.crypto.historyTitle', 'История выводов')}
          </h3>
          <div className="space-y-2">
            {history.map((item) => {
              const statusConfig = getStatusConfig(item.status)
              const StatusIcon = statusConfig.icon

              return (
                <div
                  key={item.id}
                  className={`rounded-lg border p-3 ${statusConfig.containerClass}`}
                >
                  <div className="flex items-center gap-3">
                    <StatusIcon size={18} className={statusConfig.iconClass} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <bdi dir="ltr" className="text-sm font-medium text-white">#{item.id}</bdi>
                        <span className="text-xs text-text-secondary"><bdi>{item.asset}</bdi> · <bdi>{item.network_title}</bdi></span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-text-secondary">
                          <bdi dir="ltr">{item.crystals.toLocaleString('en-US')}</bdi> <Gem size={12} className="inline text-accent-primary" /> → <bdi dir="ltr">{item.amount} {item.asset}</bdi>
                        </span>
                      </div>
                      <div dir="ltr" className="text-xs text-text-secondary mt-0.5 break-all">{item.address}</div>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-white border-opacity-5">
                    <div className={`text-xs font-medium ${statusConfig.textClass}`}>
                      {statusConfig.label}
                      {item.status === 'success' && item.finished_at && (
                        <bdi className="text-text-secondary ms-2">· {item.finished_at}</bdi>
                      )}
                    </div>
                    {item.tx_hash && (
                      <button
                        type="button"
                        onClick={() => handleCopyTxHash(item.tx_hash)}
                        className="mt-1 flex items-center gap-1.5 text-xs text-text-secondary hover:text-accent-primary transition-colors break-all text-start"
                      >
                        <Copy size={12} className="flex-shrink-0" />
                        <bdi dir="ltr" className="font-mono break-all">{item.tx_hash}</bdi>
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default CryptoWithdraw
